/* This component should subtitute MultiDatePicker once DEV-95888 is done */

import React, { useEffect, useId, useMemo } from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import { t } from '@lingui/macro';
import moment from 'moment';

import { parseDatePopoverErrors } from '@rover/components/src/datetime/DatePicker/DatePicker';
import ScrollableDayPicker from '@rover/components/src/datetime/ScrollableDayPicker/ScrollableDayPicker';
import FormBasicValidationError from '@rover/components/src/formFields/FormValidationError/FormBasicValidationError';
import InlineErrorDateInput from '@rover/components/src/formFields/InlineErrorDateInput/InlineErrorDateInputGeneric';
import { Box, Button, Flex, Heading, Paragraph } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import useTheme from '@rover/kibble/themes/useTheme';
import LabelAndErrorFormGroup from '@rover/react-lib/src/components/utils/LabelAndErrorFormGroup';
import dayFactory from '@rover/react-lib/src/factories/dayFactory';
import useOnClickOutside from '@rover/react-lib/src/hooks/useOnClickOutside';
import { useSchedulerForm } from '@rover/react-lib/src/pages/contact-sitter/ContactSitterPage/components/ServiceScheduler/contexts/SchedulerFormContext';
import { computeAriaLabelFromDays, getStringFromDates } from '@rover/react-lib/src/utils/datetime';
import type { Day } from '@rover/types/src/datetime/Day';
import getDateTimeFormatMapForLang from '@rover/utilities/datetime';

import {
  createRenderDayForHolidays,
  DatePickerCaption,
  DatePickerNavbar,
  DatePickerStylingWrapper,
} from '../DatePicker';
import { MultiDatePickerProps, numberOfMonthsAllowed, StyledCloseIcon } from '../GenericCalendar';

type DayPickerClickModifiers = {
  disabled: boolean;
  selected: boolean;
};

type KeyboardEvent = {
  key: string;
  preventDefault(): void;
};

function checkDateErrors(
  selectedDays: Day[],
  insideBottomSheet: boolean = false
): undefined | string {
  if (selectedDays.length > 0) {
    return undefined;
  }
  if (insideBottomSheet) {
    return t`Select your service dates`;
  }
  return t`Select at least one day`;
}

function handleDayClick<T extends Day>(
  selectedDays: T[],
  onChange: (days: Array<Day & T>) => void,
  clickedDate: Date,
  // eslint-disable-next-line default-param-last
  { selected, disabled }: DayPickerClickModifiers = { selected: false, disabled: false },
  language: string
): void {
  if (disabled) return;
  if (selected) {
    const selectedDates = selectedDays.map((d) => d.date);
    const selectedIndex = selectedDates.findIndex((d) => d && DateUtils.isSameDay(d, clickedDate));
    selectedDays.splice(selectedIndex, 1);
  } else {
    const DF = getDateTimeFormatMapForLang(language);
    selectedDays.push(
      // @ts-expect-error TS is mad we're assigning a Day to T, when T could have extra props.  This isn't great but the consumers of the component seem to handle it fine
      dayFactory({
        name: moment(clickedDate).format(DF.MONTH_DAY_MED),
        date: clickedDate,
      })
    );
  }
  onChange([...selectedDays]);
}

function clearDaysSelection<T extends Day>(
  selectedDays: T[],
  onChange: (days: Array<Day & T>) => void
): void {
  selectedDays.splice(0, selectedDays.length);
  onChange([...selectedDays]);
}

function MultiDatePicker({
  className = '',
  disableAfterDateTime,
  disableBeforeDateTime = new Date(),
  holidays = [],
  initialMonth,
  language,
  onChange,
  disabledDays = [],
  selectedDays,
  newCalendarInteraction = false,
  serviceName,
  serviceSubtitle,
  maxDate,
  fromMonth,
  validationType,
  isMobileBrowser,
  isDismissible,
  ...other
}: MultiDatePickerProps<Day>): JSX.Element {
  const dayPickerProps = {
    fromMonth: fromMonth || disableBeforeDateTime,
    toMonth: disableAfterDateTime,
    initialMonth,
    disabledDays: [{ before: disableBeforeDateTime, after: disableAfterDateTime }, ...disabledDays],
    selectedDays: selectedDays.map((d) => d.date),
    onDayClick: (clickedDate, modifiers) =>
      handleDayClick<Day>(selectedDays, onChange, clickedDate, modifiers, language),
    navbarElement: DatePickerNavbar,
    captionElement: DatePickerCaption,
    localeUtils: MomentLocaleUtils,
    locale: language,
    modifiers: {
      holiday: holidays,
    },
    renderDay: createRenderDayForHolidays(holidays),
  };

  const { borderWidth, colors, radii, sizes } = useTheme();
  const [showCalendar, setShowCalendar] = React.useState<boolean | null>(null);
  const calendarWrapperId = useId();
  const calendarWrapperRef = React.useRef<HTMLDivElement>(null);
  const [localError, setLocalError] = React.useState<undefined | string>(undefined);
  const { isDirty, onFormDirtinessChanged } = useSchedulerForm();
  const DF = useMemo(() => getDateTimeFormatMapForLang(language), [language]);

  useOnClickOutside(calendarWrapperRef, () => {
    if (showCalendar) {
      setShowCalendar(false);
      setLocalError(checkDateErrors(selectedDays));
    }
  });

  useEffect(() => {
    if (showCalendar === false) setLocalError(checkDateErrors(selectedDays));
  }, [showCalendar]);

  useEffect(() => {
    if (!isDirty) {
      setLocalError(undefined);
    }
  }, [isDirty]);

  useEffect(() => {
    if (localError) {
      onFormDirtinessChanged?.(true);
    }
  }, [localError, onFormDirtinessChanged]);

  const renderInput = (validationError, isMobile = false): React.ReactNode => {
    const dateInputString = getStringFromDates(selectedDays);
    const ariaLabel = other['aria-label'] || computeAriaLabelFromDays(selectedDays, DF);

    return (
      <Box
        width={isMobile && showCalendar ? '80vw' : 'unset'}
        position="relative"
        marginBottom="6x"
      >
        <InlineErrorDateInput
          onClick={(): void => {
            if (isMobile && showCalendar) {
              return;
            }
            setShowCalendar(!showCalendar);
          }}
          onKeyDown={(event: KeyboardEvent): void => {
            if (event.key === 'Enter') {
              setShowCalendar(!showCalendar);
            } else if (event.key === ' ') {
              event.preventDefault();
              setShowCalendar(!showCalendar);
            }
          }}
          value={typeof dateInputString === 'string' ? dateInputString : dateInputString}
          errorMessage={(validationError || localError) && checkDateErrors(selectedDays)}
          icon={selectedDays.length >= 1 && <StyledCloseIcon data-testid="close-icon" />}
          onIconClick={(): void => {
            if (selectedDays.length >= 1) {
              clearDaysSelection(selectedDays, onChange);
              setLocalError(checkDateErrors(selectedDays));
            } else {
              setShowCalendar(!showCalendar);
            }
          }}
          aria-expanded={showCalendar}
          aria-controls={calendarWrapperId}
          {...(ariaLabel
            ? { 'aria-label': ariaLabel }
            : { 'aria-labelledby': other['aria-labelledby'] })}
        />
      </Box>
    );
  };

  const renderMobile = (formGroupProps): JSX.Element => {
    return (
      <BottomSheet
        isOpen={!!showCalendar}
        onRequestClose={() => setShowCalendar(false)}
        isDismissible={isDismissible}
      >
        <DatePickerStylingWrapper
          className={className}
          styleAsError={!!(other.validationError || localError)}
          isMobileBrowser={newCalendarInteraction && isMobileBrowser}
          newCalendarInteraction={newCalendarInteraction}
          ref={isMobileBrowser ? null : calendarWrapperRef}
        >
          <Flex flexDirection="column" alignItems="center">
            <A11yHiddenBox aria-live="assertive" role="alert">
              {other['aria-label'] || computeAriaLabelFromDays(selectedDays, DF)}
            </A11yHiddenBox>
            <Heading size="200" mb="1x">
              {serviceName as string}
            </Heading>
            <Paragraph
              size="100"
              mb="6x"
              textAlign="center"
              webProps={{ maxWidth: '80vw' }}
              textColor="secondary"
            >
              {serviceSubtitle as string}
            </Paragraph>
            <ScrollableDayPicker
              wrapperStyles={{ overflow: 'auto', height: '350px', width: '100%' }}
              initialMonth={dayPickerProps.initialMonth}
            >
              <DayPicker
                {...formGroupProps}
                {...dayPickerProps}
                initialMonth={undefined}
                numberOfMonths={numberOfMonthsAllowed(maxDate)}
                canChangeMonth={false}
              />
            </ScrollableDayPicker>
            {localError && (
              <Flex marginLeft="0x" width="100%">
                <FormBasicValidationError
                  errorMessage={checkDateErrors(selectedDays) as string}
                  id="input-error"
                />
              </Flex>
            )}
            <Button
              mt="6x"
              size="small"
              variant="primary"
              height={sizes['8x']}
              padding="0x"
              data-testid="save-selection-button"
              fullWidth
              onClick={(): void => {
                const error = checkDateErrors(selectedDays);
                setLocalError(error);
                if (!error) {
                  setShowCalendar(false);
                }
              }}
            >
              {t`Save dates`}
            </Button>
            <Button
              mt="3x"
              size="small"
              variant="flat"
              height={sizes['8x']}
              fullWidth
              padding="0x"
              onClick={(): void => clearDaysSelection(selectedDays, onChange)}
              data-testid="clear-selection-button"
            >
              {t`Clear dates`}
            </Button>
          </Flex>
        </DatePickerStylingWrapper>
      </BottomSheet>
    );
  };

  const renderDesktop = (formGroupProps): JSX.Element => {
    return (
      <Box
        pb="0x"
        mt="5x"
        sx={{ backgroundColor: 'white' }}
        width="640px"
        border={`${borderWidth.primary} solid ${colors.border.primary}`}
        borderRadius={radii.secondary}
      >
        <DayPicker {...formGroupProps} {...dayPickerProps} numberOfMonths={2} />
        <Flex flexDirection="row" justifyContent="space-between" margin="6x" marginTop="3x">
          <Button
            onClick={() => clearDaysSelection(selectedDays, onChange)}
            webProps={{ type: 'button' }}
            width={180}
            paddingY="2x"
            data-testid="clear-selection-button"
            aria-label="Clear selection"
            size="small"
          >
            {t`Reset dates`}
          </Button>
          <Button
            onClick={() => setShowCalendar(false)}
            webProps={{ type: 'button' }}
            width={180}
            variant="primary"
            paddingY="2x"
            data-testid="save-selection-button"
            aria-label="Save dates"
            size="small"
          >
            {t`Save dates`}
          </Button>
        </Flex>
      </Box>
    );
  };

  return (
    <LabelAndErrorFormGroup
      {...other}
      validationError={parseDatePopoverErrors(other.validationError, newCalendarInteraction)}
      validationType={validationType}
      useLabelElementAsCaption={false}
    >
      {(formGroupProps): JSX.Element => (
        <DatePickerStylingWrapper
          data-testid="multi-date-picker-generic"
          id={calendarWrapperId}
          className={className}
          styleAsError={!!(other.validationError || localError)}
          isMobileBrowser={newCalendarInteraction && isMobileBrowser}
          newCalendarInteraction={newCalendarInteraction}
          ref={isMobileBrowser ? null : calendarWrapperRef}
        >
          {newCalendarInteraction &&
            renderInput(other.validationError || localError, isMobileBrowser)}
          {newCalendarInteraction &&
            showCalendar &&
            isMobileBrowser &&
            renderMobile(formGroupProps)}
          {newCalendarInteraction &&
            showCalendar &&
            !isMobileBrowser &&
            renderDesktop(formGroupProps)}
          {!newCalendarInteraction && <DayPicker {...formGroupProps} {...dayPickerProps} />}
        </DatePickerStylingWrapper>
      )}
    </LabelAndErrorFormGroup>
  );
}

export default MultiDatePicker;
